import { useCallback, useEffect, useState } from 'react';

import { Box, Point, stripKeys } from '@/utils';

export type VersionedBox = Box & { v: number };

type UseSelectionBoxOpts = {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onSelectStart?: (box: Box) => void;
  onSelectMove?: (box: Box) => void;
  onSelectEnd?: (box: Box) => void;
  canvasVersion?: number;
};

export const useSelectionBox = ({
  canvasRef,
  onSelectStart,
  onSelectMove,
  onSelectEnd,
  canvasVersion = 0,
}: UseSelectionBoxOpts) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState<VersionedBox | null>(null);
  const [start, setStart] = useState<Point | null>(null);

  const clearRect = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);
  }, [canvasRef]);

  const clear = useCallback(() => {
    clearRect();
    setSelectionBox(null);
    setIsSelecting(false);
    setStart(null);
  }, [clearRect]);

  useEffect(() => {
    if (!canvasVersion) return;
    clear();
  }, [canvasVersion, clear]);

  const getRelativePos = useCallback(
    (e: MouseEvent): Point | undefined => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      return {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
    },
    [canvasRef]
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      const start = getRelativePos(e);
      if (!start) return;

      setStart(start);
      setIsSelecting(true);
      const box = { x: start.x, y: start.y, w: 0, h: 0, v: canvasVersion };
      setSelectionBox(box);

      onSelectStart?.(stripKeys(box, ['v']));
    },
    [canvasVersion, getRelativePos, onSelectStart]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isSelecting || !start) return;

      const pos = getRelativePos(e);
      if (!pos) return;

      const box = {
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        w: Math.abs(pos.x - start.x),
        h: Math.abs(pos.y - start.y),
        v: canvasVersion,
      };
      setSelectionBox(box);

      onSelectMove?.(stripKeys(box, ['v']));
    },
    [canvasVersion, getRelativePos, isSelecting, onSelectMove, start]
  );

  const handleMouseUp = useCallback(() => {
    clearRect();
    setIsSelecting(false);

    if (selectionBox) {
      onSelectEnd?.(stripKeys(selectionBox, ['v']));
    }
  }, [clearRect, onSelectEnd, selectionBox]);

  useEffect(() => {
    let frameId: number | null = null;

    if (frameId != null) cancelAnimationFrame(frameId);

    frameId = requestAnimationFrame(() => {
      if (!selectionBox || !canvasVersion || !isSelecting) return;

      if (selectionBox.v !== canvasVersion) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return;

      const dpr = window.devicePixelRatio || 1;
      const width = Math.round(canvas.width / dpr);
      const height = Math.round(canvas.height / dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      clearRect();

      const x = selectionBox.x * width;
      const y = selectionBox.y * height;
      const w = selectionBox.w * width;
      const h = selectionBox.h * height;

      const color = '#32B5FF';
      context.strokeStyle = color;
      context.fillStyle = `${color}1A`;
      context.lineWidth = 1.5;
      context.strokeRect(x, y, w, h);
      context.fillRect(x, y, w, h);
    });

    return () => {
      if (frameId != null) cancelAnimationFrame(frameId);
    };
  }, [canvasRef, canvasVersion, clearRect, isSelecting, selectionBox]);

  return { isSelecting, selectionBox, handleMouseDown, handleMouseMove, handleMouseUp, clear };
};
